import React, { useState, useEffect } from 'react';
import { useDidUpdate } from '../../Helpers/useDidUpdate';
import { useToasts } from 'react-toast-notifications';
import { Prompt } from 'react-router';
import { Field, CancelButton, DeleteButton } from '@galop/component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faInfoCircle, faEdit, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Button, Label, Nav, NavItem, NavLink, Popover, PopoverHeader, PopoverBody, ListGroup, ListGroupItem } from 'reactstrap';
import { makeCall } from '../../auth-utils';
import { api, globalErrorHandler } from '../../api/create-api';
import { GetOptionText } from '../../Helpers/Helper';
import { Program } from './Program';
import NumberFormat from 'react-number-format';
import Lock, { AppIsLocked } from '../Shared/Lock';

export const AssetInfo = (props) => {
    const ls = require('local-storage');
    const { addToast, removeToast } = useToasts();
    const { selectedAsset, setAssetInfo, editAsset, questions, policies, setEditing, Close } = props;
    const [asset, setAsset] = useState(props.assetInfo);
    const [assetIsDirty, setAssetIsDirty] = useState(false);
    const [showAddress, setShowAddress] = useState(false);
    const [totalReceived, setTotalReceived] = useState(null);
    const [editProgram, setEditProgram] = useState({});
    const [savingData, setSavingData] = useState(false);
    const editable = !AppIsLocked();

    useDidUpdate(() => {
        if (assetIsDirty) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = null;
        }
    });

    useEffect(() => setEditing(assetIsDirty), [assetIsDirty]);

    useEffect(() => {
        return () => {
            window.onbeforeunload = null;
        };
    }, []);

    useEffect(() => {
        if (props.assetInfo === null) {
            addToast(`Loading ${selectedAsset.name}`, { appearance: 'info', id: 'loading-asset' });
        }
        else {
            removeToast('loading-asset');
        }
        setAsset(props.assetInfo);
    }, [props.assetInfo]);

    useEffect(() => {
        //getAssetInfo();
        getTotalERAReceived();
    }, [editAsset]);

    //const getAssetInfo = () => {
    //    if (selectedAsset) {
    //        makeCall(async (authHeader) => {
    //            const result = await api(`api/assets/details/${selectedAsset.id}`, { headers: authHeader })
    //                .catch(error => globalErrorHandler(error, addToast));

    //            if (result && result.data) {
    //                setAsset(result.data);
    //                ls('selected-asset', result.data);
    //            }
    //        });
    //    }
    //};
    const deleteProgram = (index) => {
        const newPrograms = asset.eraPrograms;
        newPrograms.splice(index, 1);
        setAsset({ ...asset, eraPrograms: [...newPrograms] });
        setAssetIsDirty(true);
    }

    const AllRequiredAreValid = () => {
        let isValid = true;

        /* Check the Questions */
        questions.filter(question => ["Eviction", "CDC", "ERA"].includes(question.type)).forEach(question => {
            isValid = isValid && !!asset.answers.find(answer => answer.questionId === question.id);
            if (isValid && getAnswer(question.id) === question.showNotesOption && !getAnswer(question.id, 'notes')) {
                isValid = false;
            }
        });

        /* ERA Programs */
        if (asset.eraPrograms) {
            asset.eraPrograms.forEach((program, index) => {
                isValid = isValid && !!program.name;
                questions.filter(question => question.type === 'ERAProgram').forEach(question => {
                    isValid = isValid && !!(program.answers || []).find(answer => answer.questionId === question.id);
                });
            });
        }

        return isValid;
    };

    const AllValid = () => {
        let isValid = true;

        /* Duplicate ERA Programs */
        if (asset.eraPrograms) {
            asset.eraPrograms.filter(pr=> !pr.deleted).forEach((program, index) => {
                isValid = isValid && !asset.eraPrograms.filter(pr => !pr.deleted).filter((pr, i) => i !== index).find(pr => pr.name === program.name && !pr.deleted);
            });
        }

        return isValid;
    };

    const saveAsset = (callback) => {
        if (assetIsDirty) {
            if (AllRequiredAreValid()) {
                if (AllValid()) {
                    if (asset.eraPrograms) {
                        asset.eraPrograms.forEach(program => {
                            if (!isNaN(program.id)) {
                                delete program.id;
                            }
                        });
                    }
                    makeCall(async (authHeader) => {
                        let method = 'put';

                        setSavingData(true);
                        const result = await api({
                            url: `api/assets`,
                            method,
                            data: asset,
                            headers: authHeader
                        })
                            .catch(error => {
                                setSavingData(false);
                                globalErrorHandler(error, addToast);
                            });

                        if (result && result.status === 200) {
                            setAssetInfo(asset);
                            Close();

                            if (callback && typeof callback === 'function') {
                                callback();
                            }
                        }
                    });
                }
                else {
                    addToast('Two programs have the same name. Please change one of them.', { appearance: 'error', autoDismiss: true });
                }
            }
            else {
                addToast('A required field missing. Please fill in all the fields.', { appearance: 'error', autoDismiss: true });
            }
        }
    };

    const updateQuestion = (field, value, id) => {
        let newAnswers = asset['answers'] || [];
        const answer = newAnswers.find(record => record.questionId === id) || {};
        const index = newAnswers.indexOf(answer);

        if (!!value) {
            answer.questionId = id;
            answer.assetId = asset.id;
            answer[field] = value;

            if (answer.answer !== GetOptionText(questions, id, 'id', 'showNotesOption')) {
                delete answer.notes;
            }

            if (index === -1) {
                newAnswers.push(answer);
            }
        }
        else {
            newAnswers.splice(index, 1);
        }

        setAsset({ ...asset, answers: newAnswers });
        setAssetIsDirty(true);
    };

    const getAnswer = (id, field = 'answer') => {
        return ((asset.answers || []).find(answer => answer.questionId === id) || {})[field];
    }

    const addProgram = () => {
        setAsset({ ...asset, eraPrograms: [{ new: true }, ...(asset.eraPrograms || [])] });
        setAssetIsDirty(true);
        setEditProgram({ [0]: true });
    };

    const updateProgram = (field, value, program, index) => {
        /* Set Value */
        if (value) {
            program[field] = value;
        }
        else {
            delete program[field];
        }

        if (field === 'name') {
            if ((asset.eraPrograms || []).filter((pr, i) => i !== index).filter(pr=> !pr.deleted).find(pr => pr.name === program.name)) {
                program.invalidName = true;
            }
            else {
                delete program.invalidName;
            }
        }

        asset.eraPrograms.splice(index, 1, program);
        setAsset({ ...asset, eraPrograms: asset.eraPrograms });
        setAssetIsDirty(true);
    };

    const updateProgramQuestion = (field, value, id, program, index) => {
        let newAnswers = program['answers'] || [];
        const answer = newAnswers.find(record => record.questionId === id) || {};
        const answerIndex = newAnswers.indexOf(answer);

        answer.questionId = id;
        answer.assetId = asset.id;
        if (field === 'answers') {
            if (!answer[field]) {
                answer[field] = [];
            }

            if (!answer[field].includes(value)) {
                answer[field].push(value);
            }
            else {
                answer[field] = [...answer[field].filter(ans => ans && ans !== value)]
            }
            answer.answer = answer[field].join('|');
        }
        else if (field === 'answer') {
            answer[field] = value;
            answer.answers = [value];
        }
        else {
            answer[field] = value;
        }

        if (answerIndex === -1) {
            newAnswers.push(answer);
        }

        asset.eraPrograms.splice(index, 1, { ...program, answers: newAnswers });
        setAsset({ ...asset, eraPrograms: asset.eraPrograms });
        setAssetIsDirty(true);
    };

    const removeProgram = (index) => {
        const newPrograms = [...(asset.eraPrograms || []).filter((p, i) => i !== index)];
        if (newPrograms.length > 0) {
            setAsset({ ...asset, eraPrograms: newPrograms });
        }
        else {
            delete asset.eraPrograms;
            setAsset({ ...asset });
        }

        setAssetIsDirty(true);
        delete editProgram[index];
        setEditProgram(editProgram);
    };

    const getTotalERAReceived = () => {
        if (selectedAsset) {
            makeCall(async (authHeader) => {
                const result = await api(`api/assets/total-received/${selectedAsset.id}`, {
                    headers: authHeader
                })
                    .catch(error => globalErrorHandler(error, addToast));

                if (result && result.data) {
                    setTotalReceived(result.data);
                }
            });
        }
    };

    return (asset ?
        <div className={`${props.className} mb-5 mb-sm-3`}>
            <Prompt when={assetIsDirty} message='You have unsaved changes, are you sure you want to leave?' />
            <Row className="mb-3">
                <Col>
                    <div>{asset.streetAddress}</div>
                    <div>{asset.city}{asset.state ? `, ${asset.state}` : null} {asset.postalCode}</div>
                    {asset.country && asset.country !== "US" &&
                        <div>{asset.country}</div>
                    }
                </Col>
                <Col className="text-right">
                    {editAsset &&
                        <CancelButton color="light" disabled={savingData} className="border mx-1" ShowDropdown={assetIsDirty} Close={() => {
                            if (Close && typeof Close === 'function') {
                                Close();
                            }
                        }}>Cancel</CancelButton>
                    }
                    <Lock
                        unlocked={() =>
                            <Button color="success" disabled={!assetIsDirty || savingData} onClick={() => {
                                saveAsset();
                            }}>Save</Button>
                        }
                    />
                </Col>
            </Row>
            <div /*className="p-2"*/>
                {!editAsset &&
                    <div className="font-italic"><small><FontAwesomeIcon icon={faExclamationCircle} /> Please fill out this information about your community before managing the lease detail records.</small></div>
                }
                <Row>
                    <Col lg="7">
                        <div className="mb-3">
                            <fieldset>
                                <legend className="border-bottom">Evictions</legend>
                                <Row>
                                    {questions && questions.filter(question => question.type === 'Eviction').map((question, index) =>
                                        <Col md="6" key={question.id} /*className="pb-2 border-bottom mb-2"*/>
                                            <Label className="mt-2 mb-0">{question.text}</Label>
                                            <Field type="select" HideHelp HideLabel fieldClassName={getAnswer(question.id) ? '' : 'is-invalid'} className="mb-3" field={`answer_${question.id}`} value={getAnswer(question.id)} label={question.text} EnableInput={{ disabled: !editable }} onChange={(e) => updateQuestion('answer', e.target.value, question.id)}>
                                                <option value="">Select an Answer</option>
                                                {question.options.sort((a, b) => a.order - b.order).map(option => <option key={`${option.questionId}_${option.optionText}`} value={option.optionText}>{option.optionText}</option>)}
                                            </Field>
                                            {question.notesText && getAnswer(question.id) === question.showNotesOption &&
                                                <Field type="text" fieldClassName={getAnswer(question.id, 'notes') ? '' : 'is-invalid'} label={question.notesText} field={`notes_${question.id}`} value={getAnswer(question.id, 'notes')} EnableInput={{ disabled: !editable }} HideHelp onChange={(e) => updateQuestion('notes', e.target.value, question.id)} />
                                            }
                                        </Col>
                                    )}
                                </Row>
                            </fieldset>
                        </div>
                    </Col>
                    <Col>
                        <div className="mb-3">
                            <fieldset className="mb-2">
                                <legend className="border-bottom">CDC Declarations</legend>
                                <Row>
                                    {questions && questions.filter(question => question.type === 'CDC').map((question, index) =>
                                        <Col xs="12" key={question.id} /*className="pb-2 border-bottom mb-2"*/>
                                            <div className="mb-2">
                                                <Label className="mt-2 mb-0">{question.text}</Label>
                                                <Row>
                                                    <Col xs="7" sm="6" md="5" lg="7" xl="6">
                                                        <Field type="select" HideHelp HideLabel fieldClassName={getAnswer(question.id) ? '' : 'is-invalid'} className="mb-3" field={`answer_${question.id}`} value={getAnswer(question.id)} label={question.text} EnableInput={{ disabled: !editable }} onChange={(e) => updateQuestion('answer', e.target.value, question.id)}>
                                                            <option value="">Select an Answer</option>
                                                            {question.options.sort((a, b) => a.order - b.order).map(option => <option key={`${option.questionId}_${option.optionText}`} value={option.optionText}>{option.optionText}</option>)}
                                                        </Field>
                                                    </Col>
                                                </Row>
                                                {question.notesText && getAnswer(question.id) === question.showNotesOption &&
                                                    <Field type="text" fieldClassName={getAnswer(question.id, 'notes') ? '' : 'is-invalid'} label={question.notesText} field={`notes_${question.id}`} value={getAnswer(question.id, 'notes')} EnableInput={{ disabled: !editable }} HideHelp onChange={(e) => updateQuestion('notes', e.target.value, question.id)} />
                                                }
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </fieldset>
                        </div>
                    </Col>
                    {questions && questions.filter(question => question.type === 'Policy').length > 0 &&
                        <Col>
                            <div className="mb-3">
                                <fieldset>
                                    <legend className="border-bottom">General Policies</legend>
                                    <Row>
                                        {questions && questions.filter(question => question.type === 'Policy').map((question, index) =>
                                            <Col xs="12" key={question.id} /*className="pb-2 border-bottom mb-2"*/>
                                                <div className="mb-2">
                                                    <Label className="mt-2 mb-0">{question.text}</Label>
                                                    <Field type="select" HideHelp HideLabel fieldClassName={getAnswer(question.id) ? '' : 'is-invalid'} className="mb-3" field={`answer_${question.id}`} value={getAnswer(question.id)} label={question.text} EnableInput={{ disabled: !editable }} onChange={(e) => updateQuestion('answer', e.target.value, question.id)}>
                                                        <option value="">Select an Answer</option>
                                                        {question.options.sort((a, b) => a.order - b.order).map(option => <option key={`${option.questionId}_${option.optionText}`} value={option.optionText}>{option.optionText}</option>)}
                                                    </Field>
                                                    {question.notesText && getAnswer(question.id) === question.showNotesOption &&
                                                        <Field type="text" fieldClassName={getAnswer(question.id, 'notes') ? '' : 'is-invalid'} label={question.notesText} field={`notes_${question.id}`} value={getAnswer(question.id, 'notes')} EnableInput={{ disabled: !editable }} HideHelp onChange={(e) => updateQuestion('notes', e.target.value, question.id)} />
                                                    }
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </fieldset>
                            </div>
                        </Col>
                    }
                </Row>
                <div>
                    <fieldset className="mb-2">
                        <legend className="border-bottom">Emergency Rental Assistance</legend>
                        <Row>
                            <Col>
                                <div className="d-flex align-items-center">
                                    <div className="">
                                        <span>Available ERA Programs</span>
                                    </div>
                                    <Lock
                                        unlocked={() =>
                                            <div className="mx-1">
                                                <Button size="sm" color="success" outline onClick={addProgram}><FontAwesomeIcon icon={faPlus} /></Button>
                                            </div>
                                        }
                                    />
                                    {totalReceived && <div className="flex-fill text-right">
                                        <span className="text-dark">Total Received:</span> <NumberFormat prefix="$" displayType="text" value={totalReceived} thousandSeparator decimalScale="2" />
                                    </div>}
                                </div>
                                <Program Header {...{ questions }} />
                                <div className="border rounded scrollable-list p-1 pr-2" style={{ height: 300, overflow: 'scroll' }}>
                                    {asset.eraPrograms && asset.eraPrograms.map((program, index) =>
                                        (!program.deleted &&
                                            <Program key={`${asset.id}_${index}`} {...{ program, updateProgramQuestion, questions, updateProgram, index }}
                                                showBackground={asset.eraPrograms.filter(p => !p.deleted).indexOf(program) % 2}
                                                RemoveProgram={() => removeProgram(index)}
                                                editProgram={editProgram[index]}
                                                setEditProgram={() => setEditProgram({ ...editProgram, [index]: !editProgram[index] })}
                                            />
                                        )
                                    )}
                                </div>
                            </Col>
                            <Col lg="4" xl="3">
                                <Label>&nbsp;</Label>
                                {questions && questions.filter(question => question.type === 'ERA').map((question, index) =>
                                    <div key={question.id} className="mb-2">
                                        <Label className="mt-2 mb-0">{question.text}</Label>
                                        <Row>
                                            <Col xs="7" sm="4" lg="8" xl="9">
                                                <Field type="select" HideHelp HideLabel fieldClassName={getAnswer(question.id) ? '' : 'is-invalid'} className="mb-3" field={`answer_${question.id}`} value={getAnswer(question.id)} label={question.text} EnableInput={{ disabled: !editable }} onChange={(e) => updateQuestion('answer', e.target.value, question.id)}>
                                                    <option value="">Select an Answer</option>
                                                    {question.options.sort((a, b) => a.order - b.order).map(option => <option key={`${option.questionId}_${option.optionText}`} value={option.optionText}>{option.optionText}</option>)}
                                                </Field>
                                            </Col>
                                        </Row>
                                        {question.notesText && getAnswer(question.id) === question.showNotesOption &&
                                            <Field type="text" fieldClassName={getAnswer(question.id, 'notes') ? '' : 'is-invalid'} label={question.notesText} field={`notes_${question.id}`} value={getAnswer(question.id, 'notes')} EnableInput={{ disabled: !editable }} HideHelp onChange={(e) => updateQuestion('notes', e.target.value, question.id)} />
                                        }
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </fieldset>
                </div>
            </div>
        </div>
        : null
    );
};


// WEBPACK FOOTER //
// ./src/components/Assets/AssetInfo.js